/**
 * Column Component Definition
 *
 * A container that holds content components within a row.
 * Supports flexible width, alignment, and full styling options.
 */

import { getGapSize } from '../../renderer/StyleResolver';

export const columnComponent = {
  type: 'column',
  label: 'Column',
  icon: 'Columns',
  description: 'Container for content within a row',
  category: 'layout',

  // Container options
  canHaveChildren: true,

  resolveStyles(props) {
    const displayMode = props.display || 'flex';
    const isFlex = displayMode === 'flex';
    const gap = getGapSize(props.gap);

    return {
      display: displayMode,
      ...(isFlex ? {
        flexDirection: 'column',
        gap,
        alignItems: props.alignItems || 'stretch',
        justifyContent: props.justifyContent || 'flex-start',
      } : displayMode === 'grid' ? { gap } : {}),
      minWidth: 0,
      position: props.position || 'relative',
    };
  },

  defaultProps: {
    // Layout
    display: 'flex',
    gap: 'md',
    alignItems: 'stretch',
    justifyContent: 'flex-start',

    // Size (span is for grid-based layouts, responsive for breakpoint-specific widths)
    span: { base: 6, tablet: null, desktop: null },
    width: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    height: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    minWidth: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    minHeight: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    maxWidth: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    maxHeight: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },

    // Spacing
    padding: { base: { top: 16, bottom: 16, left: 16, right: 16, unit: 'px' }, tablet: null, desktop: null },
    margin: { base: { top: 0, bottom: 0, left: 0, right: 0, unit: 'px' }, tablet: null, desktop: null },

    // Appearance
    backgroundColor: 'transparent',
    backgroundImageUrl: '',
    backgroundSize: 'cover',
    backgroundPosition: 'center',

    // Border
    borderWidth: 0,
    borderStyle: 'solid',
    borderColor: 'transparent',
    borderRadius: 0,

    // Advanced
    overflow: 'visible',
    visibility: { base: true, tablet: null, desktop: null },

    // Positioning
    position: 'relative',
    top: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    right: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    bottom: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    left: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    zIndex: 'auto',

    // Custom CSS
    customCSS: '',

    // Flex child (when inside a flex parent) — responsive
    flexChild: { base: { mode: 'auto', basis: 50 }, tablet: null, desktop: null },
  },

  settingsSchema: [
    // Layout category
    {
      key: 'display',
      type: 'iconToggle',
      label: 'Display',
      category: 'Layout',
      default: 'flex',
      options: [
        { value: 'block', label: 'Block', icon: 'Square' },
        { value: 'flex', label: 'Flex', icon: 'Rows3' },
        { value: 'none', label: 'None', icon: 'EyeOff' },
      ],
    },
    {
      key: 'gap',
      type: 'select',
      label: 'Gap',
      category: 'Layout',
      default: 'md',
      options: [
        { value: 'none', label: 'None' },
        { value: 'sm', label: 'Small (8px)' },
        { value: 'md', label: 'Medium (16px)' },
        { value: 'lg', label: 'Large (24px)' },
        { value: 'xl', label: 'Extra Large (32px)' },
      ],
      showWhen: { field: 'display', in: ['flex', undefined] },
    },
    {
      key: 'alignItems',
      type: 'iconToggle',
      label: 'Align',
      category: 'Layout',
      default: 'stretch',
      options: [
        { value: 'flex-start', label: 'Start', icon: 'AlignStartVertical' },
        { value: 'center', label: 'Center', icon: 'AlignCenterVertical' },
        { value: 'flex-end', label: 'End', icon: 'AlignEndVertical' },
        { value: 'stretch', label: 'Stretch', icon: 'AlignVerticalSpaceAround' },
        { value: 'baseline', label: 'Baseline', icon: 'Baseline' },
      ],
      showWhen: { field: 'display', in: ['flex', undefined] },
    },
    {
      key: 'justifyContent',
      type: 'iconToggle',
      label: 'Justify',
      category: 'Layout',
      default: 'flex-start',
      options: [
        { value: 'flex-start', label: 'Start', icon: 'AlignVerticalJustifyStart' },
        { value: 'center', label: 'Center', icon: 'AlignVerticalJustifyCenter' },
        { value: 'flex-end', label: 'End', icon: 'AlignVerticalJustifyEnd' },
        { value: 'space-between', label: 'Between', icon: 'AlignVerticalSpaceBetween' },
      ],
      showWhen: { field: 'display', in: ['flex', undefined] },
    },

    // Size category - span is controlled by row's columnLayout per breakpoint
    // Note: span is hidden from column settings since it's managed by the row's columnLayout
    // If users need fine-grained control, they can use "Custom" layout and adjust widths directly
    {
      key: 'width',
      type: 'responsiveDimension',
      label: 'Custom Width',
      category: 'Size',
      description: 'Override grid span with custom width',
    },
    {
      key: 'height',
      type: 'responsiveDimension',
      label: 'Height',
      category: 'Size',
    },
    {
      key: 'minWidth',
      type: 'responsiveDimension',
      label: 'Min Width',
      category: 'Size',
    },
    {
      key: 'minHeight',
      type: 'responsiveDimension',
      label: 'Min Height',
      category: 'Size',
    },
    {
      key: 'maxWidth',
      type: 'responsiveDimension',
      label: 'Max Width',
      category: 'Size',
    },
    {
      key: 'maxHeight',
      type: 'responsiveDimension',
      label: 'Max Height',
      category: 'Size',
    },

    // Spacing category
    {
      key: 'padding',
      type: 'responsivePadding',
      label: 'Padding',
      category: 'Spacing',
    },
    {
      key: 'margin',
      type: 'responsiveMargin',
      label: 'Margin',
      category: 'Spacing',
    },

    // Appearance category
    {
      key: 'backgroundColor',
      type: 'color',
      label: 'Background Color',
      category: 'Appearance',
      default: 'transparent',
      allowGradient: true,
    },
    {
      key: 'backgroundImageUrl',
      type: 'mediaPicker',
      label: 'Background Image',
      category: 'Appearance',
      acceptTypes: 'image',
      placeholder: 'https://...',
    },
    {
      key: 'backgroundSize',
      type: 'select',
      label: 'Background Size',
      category: 'Appearance',
      default: 'cover',
      options: [
        { value: 'cover', label: 'Cover' },
        { value: 'contain', label: 'Contain' },
        { value: 'auto', label: 'Auto' },
      ],
      showWhen: { field: 'backgroundImageUrl', isNotEmpty: true },
    },
    {
      key: 'backgroundPosition',
      type: 'select',
      label: 'Background Position',
      category: 'Appearance',
      default: 'center',
      options: [
        { value: 'center', label: 'Center' },
        { value: 'top', label: 'Top' },
        { value: 'bottom', label: 'Bottom' },
        { value: 'left', label: 'Left' },
        { value: 'right', label: 'Right' },
      ],
      showWhen: { field: 'backgroundImageUrl', isNotEmpty: true },
    },

    // Border category
    {
      key: 'borderWidth',
      type: 'number',
      label: 'Border Width (px)',
      category: 'Border',
      min: 0,
      max: 20,
      default: 0,
    },
    {
      key: 'borderStyle',
      type: 'select',
      label: 'Border Style',
      category: 'Border',
      default: 'solid',
      options: [
        { value: 'none', label: 'None' },
        { value: 'solid', label: 'Solid' },
        { value: 'dashed', label: 'Dashed' },
        { value: 'dotted', label: 'Dotted' },
      ],
    },
    {
      key: 'borderColor',
      type: 'color',
      label: 'Border Color',
      category: 'Border',
      default: 'transparent',
    },
    {
      key: 'borderRadius',
      type: 'number',
      label: 'Border Radius (px)',
      category: 'Border',
      min: 0,
      max: 100,
      default: 0,
    },

    // Advanced category
    {
      key: 'overflow',
      type: 'select',
      label: 'Overflow',
      category: 'Advanced',
      default: 'visible',
      options: [
        { value: 'visible', label: 'Visible' },
        { value: 'hidden', label: 'Hidden' },
        { value: 'scroll', label: 'Scroll' },
        { value: 'auto', label: 'Auto' },
        { value: 'clip', label: 'Clip' },
      ],
    },
    {
      key: 'visibility',
      type: 'responsiveVisibility',
      label: 'Visibility',
      category: 'Advanced',
      default: { base: true, tablet: null, desktop: null },
    },

    // Position category
    {
      key: 'position',
      type: 'select',
      label: 'Position',
      category: 'Position',
      default: 'relative',
      options: [
        { value: 'static', label: 'Static (Normal)' },
        { value: 'relative', label: 'Relative' },
        { value: 'absolute', label: 'Absolute' },
        { value: 'fixed', label: 'Fixed' },
        { value: 'sticky', label: 'Sticky' },
      ],
    },
    {
      key: 'top',
      type: 'responsiveDimension',
      label: 'Top',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'right',
      type: 'responsiveDimension',
      label: 'Right',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'bottom',
      type: 'responsiveDimension',
      label: 'Bottom',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'left',
      type: 'responsiveDimension',
      label: 'Left',
      category: 'Position',
      showWhen: { field: 'position', notEquals: 'static' },
    },
    {
      key: 'zIndex',
      type: 'number',
      label: 'Z-Index',
      category: 'Position',
      placeholder: 'auto',
      showWhen: { field: 'position', notEquals: 'static' },
    },

    // Custom CSS
    {
      key: 'customCSS',
      type: 'customCSS',
      label: 'Custom CSS',
      category: 'Advanced',
      default: '',
    },

    // Flex Child sizing
    {
      key: 'flexChild',
      type: 'flexChild',
      label: 'Flex Child',
      category: 'Layout',
      showWhenParent: { field: 'display', in: ['flex', 'inline-flex'] },
      description: 'Controls sizing when inside a flex container',
      default: { base: { mode: 'auto', basis: 50 }, tablet: null, desktop: null },
    },
  ],

  // Render function - will be implemented in the unified renderer
  render: null,
};

export default columnComponent;
